import { useEffect, useState } from 'react';
import { campaignStatusLabel } from '../../../../lib/workVocab';
import { defineMessages } from '../../../../i18n/define';
import { useT } from '../../../../i18n/hooks';
import { haUnit } from '../../../lib/format';
import { DateRangePicker, FormActions, FormDialog, TextArea, useFormDialogClose } from '../../../ui/forms';
import { deskApi, refusalText, windowText, type DeskCampaign, type LaunchPreview } from './desk';

export type DecisionKind =
  | 'reschedule'
  | 'pause'
  | 'resume'
  | 'complete'
  | 'cancel'
  | 'extend'
  | 'retire';

const m = defineMessages({
  titleReschedule: { id: 'ops.decision.title.reschedule', defaultMessage: 'Перенести кампанию: {work}', description: 'Заголовок окна решения (менеджер): перенести срок кампании; {work} — название работы' },
  titlePause: { id: 'ops.decision.title.pause', defaultMessage: 'Приостановить кампанию: {work}', description: 'Заголовок окна решения: приостановить кампанию; {work} — название работы' },
  titleResume: { id: 'ops.decision.title.resume', defaultMessage: 'Возобновить кампанию: {work}', description: 'Заголовок окна решения: возобновить кампанию; {work} — название работы' },
  titleComplete: { id: 'ops.decision.title.complete', defaultMessage: 'Завершить кампанию: {work}', description: 'Заголовок окна решения: завершить кампанию; {work} — название работы' },
  titleCancel: { id: 'ops.decision.title.cancel', defaultMessage: 'Отменить кампанию: {work}', description: 'Заголовок окна решения: отменить кампанию; {work} — название работы' },
  titleExtend: { id: 'ops.decision.title.extend', defaultMessage: 'Распространить на новые поля: {work}', description: 'Заголовок окна решения: добавить в кампанию поля, появившиеся позже; {work} — название работы' },
  titleRetire: { id: 'ops.decision.title.retire', defaultMessage: 'Отменить работы по выбывшим полям: {work}', description: 'Заголовок окна решения: отменить работы по полям, вышедшим из агроцикла; {work} — название работы' },
  confirmReschedule: { id: 'ops.decision.confirm.reschedule', defaultMessage: 'Перенести', description: 'Кнопка подтверждения в окне переноса кампании' },
  confirmPause: { id: 'ops.decision.confirm.pause', defaultMessage: 'Приостановить', description: 'Кнопка подтверждения: приостановить кампанию' },
  confirmResume: { id: 'ops.decision.confirm.resume', defaultMessage: 'Возобновить', description: 'Кнопка подтверждения: возобновить кампанию' },
  confirmComplete: { id: 'ops.decision.confirm.complete', defaultMessage: 'Завершить', description: 'Кнопка подтверждения: завершить кампанию' },
  confirmCancel: { id: 'ops.decision.confirm.cancel', defaultMessage: 'Отменить кампанию', description: 'Кнопка подтверждения: отменить кампанию (необратимо для работ без отчёта)' },
  confirmExtend: { id: 'ops.decision.confirm.extend', defaultMessage: 'Распространить', description: 'Кнопка подтверждения: распространить кампанию на новые поля' },
  confirmRetire: { id: 'ops.decision.confirm.retire', defaultMessage: 'Отменить работы', description: 'Кнопка подтверждения: отменить работы по выбывшим полям' },
  hintReschedule: {
    id: 'ops.decision.hint.reschedule',
    defaultMessage: 'Окно сдвинется у работы плана, кампании, хозяйств и работ на полях без отчёта. Работы на полях, по которым уже есть отчёт, сохранят прежнее окно.',
    description: 'Пояснение в окне переноса кампании (менеджер): что именно сдвинется, а что нет',
  },
  hintCancel: {
    id: 'ops.decision.hint.cancel',
    defaultMessage: 'Работы на полях без отчёта будут отменены с этой причиной. Отчёты не меняются; работу после отмены можно запустить заново.',
    description: 'Пояснение в окне отмены кампании (менеджер): последствия',
  },
  hintRetire: {
    id: 'ops.decision.hint.retire',
    defaultMessage: 'Отменяются только работы на полях без отчёта по полям, которые вышли из агроцикла. Работы с отчётом остаются.',
    description: 'Пояснение в окне отмены работ по выбывшим полям (менеджер): что затронет, а что останется',
  },
  subtitle: { id: 'ops.decision.subtitle', defaultMessage: 'Сейчас: {status}, срок {window}', description: 'Строка под заголовком окна решения; {status} — состояние кампании («идёт»), {window} — срок («5 сен – 15 окт» или «без срока»)' },
  reasonRequired: { id: 'ops.decision.error.reasonRequired', defaultMessage: 'Укажите причину решения.', description: 'Почему кнопка выключена: причина решения обязательна, а поле пустое' },
  rangeNeeded: { id: 'ops.decision.error.rangeNeeded', defaultMessage: 'Выберите новые даты окна: начало и конец.', description: 'Почему кнопка переноса выключена: окно не задано обеими датами' },
  rangeSame: { id: 'ops.decision.error.rangeSame', defaultMessage: 'Новое окно совпадает с нынешним: выберите другие даты.', description: 'Почему кнопка переноса выключена: даты не изменились' },
  newWindow: { id: 'ops.decision.newWindow', defaultMessage: 'Новое окно кампании', description: 'Подпись поля «с — по» в окне переноса кампании' },
  currentWindow: { id: 'ops.decision.currentWindow', defaultMessage: 'Сейчас: {window}', description: 'Подсказка под полем нового окна: нынешний срок кампании; {window} — «5 сен – 15 окт»' },
  extendSummary: {
    id: 'ops.decision.extend.summary',
    defaultMessage: 'Новых полей: <b>{count}</b>, земли {area}. Прежний охват и работы на полях не меняются: добавляется новый срез.',
    description: 'Итог перед распространением кампании на новые поля; {count} — число новых полей, {area} — площадь земли уже с единицей («120,50 га»); <b> — выделить число',
  },
  extendLoading: { id: 'ops.decision.extend.loading', defaultMessage: 'Считаю новые поля…', description: 'Статус в окне «Распространить», пока сервер считает новые поля' },
  warning: { id: 'ops.decision.extend.warning', defaultMessage: '{message}: {count}.', description: 'Предупреждение сервера о размещении после итога; {message} — текст сервера, {count} — число полей' },
  retireSummary: { id: 'ops.decision.retire.summary', defaultMessage: 'Выбывших полей без отчёта: {count}.', description: 'Итог в окне отмены работ по выбывшим полям; {count} — число полей' },
  reasonLabel: { id: 'ops.decision.reason.label', defaultMessage: 'Причина', description: 'Подпись поля ввода причины решения (менеджер)' },
  reasonHint: { id: 'ops.decision.reason.hint', defaultMessage: 'Обязательно: причина попадёт в журнал решений.', description: 'Подсказка под полем причины, когда она обязательна' },
  reasonPlaceholder: { id: 'ops.decision.reason.placeholder', defaultMessage: 'Что случилось и почему вы так решили', description: 'Подсказка внутри поля причины решения' },
  cancel: { id: 'ops.decision.cancel', defaultMessage: 'Отмена', description: 'Кнопка окна решения: закрыть без изменений; коротко' },
});

const TITLES = {
  reschedule: m.titleReschedule,
  pause: m.titlePause,
  resume: m.titleResume,
  complete: m.titleComplete,
  cancel: m.titleCancel,
  extend: m.titleExtend,
  retire: m.titleRetire,
} as const;

const CONFIRM = {
  reschedule: m.confirmReschedule,
  pause: m.confirmPause,
  resume: m.confirmResume,
  complete: m.confirmComplete,
  cancel: m.confirmCancel,
  extend: m.confirmExtend,
  retire: m.confirmRetire,
} as const;

/** Решения, у которых причина обязательна (сервер отклонит пустую). */
const REASON_REQUIRED: DecisionKind[] = ['reschedule', 'cancel', 'retire'];

const HINT = {
  reschedule: m.hintReschedule,
  cancel: m.hintCancel,
  retire: m.hintRetire,
} as const;

export interface DecisionDialogProps {
  kind: DecisionKind;
  campaign: DeskCampaign;
  onClose: () => void;
  onDone: () => void;
  /**
   * Предзаполненное новое окно для «Перенести» (например, после перетаскивания
   * полосы на шкале плана). Без него в поле стоит нынешнее окно кампании.
   */
  initialRange?: { from: string | null; to: string | null };
}

/**
 * Одно решение по кампании с причиной; для «Распространить» показывает новые
 * поля. Окно формы — общее (`FormDialog`): шапка и низ закреплены, причина
 * обязательна там, где её требует сервер, и об этом сказано до нажатия.
 */
export function DecisionDialog({ kind, campaign, onClose, onDone, initialRange }: DecisionDialogProps) {
  const t = useT();
  const [reason, setReason] = useState('');
  const [range, setRange] = useState({
    from: initialRange?.from ?? campaign.window_from ?? null,
    to: initialRange?.to ?? campaign.window_to ?? null,
  });
  const [preview, setPreview] = useState<LaunchPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (kind !== 'extend') return;
    deskApi
      .extendPreview(campaign.id)
      .then(setPreview)
      .catch((cause: unknown) => setError(refusalText(cause)));
  }, [kind, campaign.id]);

  const required = REASON_REQUIRED.includes(kind);
  const rangeOk = Boolean(range.from && range.to);
  const same = range.from === campaign.window_from && range.to === campaign.window_to;
  let blocker: string | null = null;
  if (kind === 'reschedule' && !rangeOk) blocker = t(m.rangeNeeded);
  else if (kind === 'reschedule' && same) blocker = t(m.rangeSame);
  else if (required && !reason.trim()) blocker = t(m.reasonRequired);

  const confirm = () => {
    if (busy || blocker) return;
    setBusy(true);
    setError(null);
    const call =
      kind === 'reschedule'
        ? deskApi.reschedule(campaign.id, range.from ?? '', range.to ?? '', reason)
        : kind === 'extend'
          ? deskApi.extend(campaign.id, reason)
          : kind === 'retire'
            ? deskApi.retireDeparted(campaign.id, reason)
            : deskApi.decide(campaign.id, kind, reason);
    call.then(onDone).catch((cause: unknown) => {
      setError(refusalText(cause));
      setBusy(false);
    });
  };

  const warn = kind === 'cancel' || kind === 'retire';
  return (
    <FormDialog
      size={kind === 'reschedule' || kind === 'extend' ? 'md' : 'sm'}
      isOpen
      onClose={onClose}
      title={t(TITLES[kind], { work: campaign.work_label })}
      subtitle={t(m.subtitle, {
        status: campaignStatusLabel(campaign.status).label,
        window: windowText(campaign.window_from, campaign.window_to),
      })}
      isDirty={(reason.trim() !== '' || (kind === 'reschedule' && !same)) && !busy}
      error={error}
      footerNote={blocker}
      footer={<DecisionActions label={t(CONFIRM[kind])} pending={busy} disabled={Boolean(blocker)} danger={warn} onConfirm={confirm} />}
    >
      {kind in HINT ? <p className={warn ? 'cdesk-note cdesk-note--warn' : 'cdesk-lede'}>{t(HINT[kind as keyof typeof HINT])}</p> : null}
      {kind === 'reschedule' ? (
        <DateRangePicker
          label={t(m.newWindow)}
          isRequired
          value={range}
          description={t(m.currentWindow, { window: windowText(campaign.window_from, campaign.window_to) })}
          onChange={setRange}
        />
      ) : null}
      {kind === 'extend' ? (
        preview ? (
          <p className="cdesk-lede">
            {t(m.extendSummary, {
              count: preview.fields_included,
              area: haUnit(preview.land_ha_included),
              b: (chunks) => <strong>{chunks}</strong>,
            })}
            {preview.warnings.map((warning) => (
              <span key={warning.code} className="cdesk-hint">
                {' '}
                {t(m.warning, { message: warning.message, count: warning.count })}
              </span>
            ))}
          </p>
        ) : error ? null : (
          <p className="cdesk-hint">{t(m.extendLoading)}</p>
        )
      ) : null}
      {kind === 'retire' ? <p className="cdesk-lede">{t(m.retireSummary, { count: campaign.departed })}</p> : null}
      <TextArea
        label={t(m.reasonLabel)}
        isRequired={required}
        value={reason}
        onChange={setReason}
        placeholder={t(m.reasonPlaceholder)}
        description={required ? t(m.reasonHint) : undefined}
        minRows={3}
        maxLength={500}
      />
    </FormDialog>
  );
}

function DecisionActions({ label, pending, disabled, danger, onConfirm }: { label: string; pending: boolean; disabled: boolean; danger: boolean; onConfirm: () => void }) {
  const t = useT();
  const close = useFormDialogClose();
  return (
    <FormActions
      secondary={{ label: t(m.cancel), onPress: close }}
      primary={{ label, onPress: onConfirm, isPending: pending, isDisabled: disabled, ...(danger ? { tone: 'danger' as const } : {}) }}
    />
  );
}
